// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartFieldPropsContainer.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * ReportPartFieldPropsContainer component
 */
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import connect from 'share/enhancers/Izenda.Connect';
import { get, getOr, merge } from 'lodash/fp';

import FormGroup from 'share/components/controls/Izenda.FormGroup';
import DropDown from 'share/components/controls/Izenda.DropDown';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import DefaultFieldPropsSchema from './Izenda.ReportPartFieldPropsSchema';
import PropertiesEditorContainer from 'share/components/controls/Izenda.PropertiesEditorContainer';
import PropertiesEditorUtils from 'share/utils/Izenda.PropertiesEditorUtils';
import ReportPartTypeManager from 'Izenda.ReportPartTypeManager';
import ToggleViewModePropertyEditor from 'share/components/controls/Izenda.ToggleViewModePropertyEditor';
import { REPORT_PART_TYPE } from 'logic/data/Izenda.ReportDesignerConstants';

// show title of group only
const UNUSED_PROPS_NULL_CONFIG_FIELD = [
  'fields.dataSource.fields',
  'fields.dataFormattings.fields',
  'fields.headerFormating.fields',
  'fields.drillDown.fields'
];

const ReportPartList = observer(function ReportPartList(props: any) {
  const reportBody = get('reportDesignerModel.reportFields.reportBody', props);
  const { configReportPart, supportReportParts } = reportBody;

  return (
    <DropDown
      blankOption
      options={supportReportParts}
      onChange={onChangeReportPart}
      valueField="id"
      labelField="headerTitle"
      value={configReportPart && configReportPart.id}
    />
  );

  function onChangeReportPart(selectedPartTitle: any, selectedPart: any) {
    reportBody && reportBody.setConfigReportPart(selectedPart);
  }
});

const ReportPartElementList = observer(function ReportPartElementList(props: any) {
  const reportBody = get('reportDesignerModel.reportFields.reportBody', props);
  const configReportPart = reportBody.configReportPart;
  const configField = configReportPart && configReportPart.configField;

  return (
    <DropDown
      blankOption
      valueField="fieldName"
      labelField="fieldName"
      value={configField && configField.fieldName}
      options={get('configurableFieldCollection', configReportPart)}
      onChange={setConfigModeToField}
    />
  );

  function setConfigModeToField(selectedElementName: any, selectedElement: any) {
    configReportPart && configReportPart.setConfigField(selectedElement);
  }
});

const ConfigFieldProperties = observer(function ConfigFieldProperties(props: any) {
  const configReportPart = get('reportDesignerModel.reportFields.reportBody.configReportPart', props) || {};
  const configField = getOr({}, 'configField', configReportPart);
  const hasConfigField = get('fieldId', configField);
  let fieldPropsSchema, reportPartType, isInvisibleField, propConfig;

  const propsParams = [
    this,
    props.reportDesignerModel,
    props.fieldStore,
    merge(props, {
      configField,
      fieldStore: props.fieldStore,
      fieldContainerDetails: configReportPart.getContainerDetails && configReportPart.getContainerDetails(configField)
    })
  ];

  if (!hasConfigField) {
    isInvisibleField = false;
    fieldPropsSchema = DefaultFieldPropsSchema;
    propConfig = fieldPropsSchema(...propsParams);
    removeUsedProps(propConfig);
  } else {
    isInvisibleField = get('field.visible', configField);
    reportPartType = ReportPartTypeManager.getReportPartType(configReportPart.type);
    fieldPropsSchema = getOr(DefaultFieldPropsSchema, 'properties.fieldProps', reportPartType);
    propConfig = fieldPropsSchema(...propsParams);
  }

  const classes = classnames('field-props-content', { 'disabled-elm': !isInvisibleField });

  return (
    <fieldset disabled={!isInvisibleField} className={classes}>
      <ScrollBox>
        <PropertiesEditorContainer propertyConfig={propConfig} />
      </ScrollBox>
    </fieldset>
  );

  function removeUsedProps(propConfig: any) {
    PropertiesEditorUtils.removeProps(propConfig, UNUSED_PROPS_NULL_CONFIG_FIELD);
  }
});

@connect()
export default class ReportPartFieldPropsContainer extends Component {
  static propTypes = {
    reportDesignerModel: PropTypes.object.isRequired,
    itemProps: PropTypes.object
  };

  render() {
    const props = this.props;
    const type = get('reportDesignerModel.reportFields.reportBody.configReportPart.reportPartContent.type', props);
    const localization = props.localization;

    return (
      <section className="form-horizontal field-props-container">
        <div className="field-props-header">
          <div className="title-group">
            <strong className="u_inlineBlock">
              {localization.getText('FIELD_PROPERTIES_SCHEMA_LABEL_FIELD_PROPERTIES')}
            </strong>
            {type !== REPORT_PART_TYPE.Form && <ToggleViewModePropertyEditor />}
          </div>
          <FormGroup className="report-parts-ctrl">
            {/* report part list*/}
            <ReportPartList {...props} />
            <ReportPartElementList {...props} />
          </FormGroup>
        </div>

        {/* fields of config report part */}
        <ConfigFieldProperties {...props} />
      </section>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/PropertiesBuilder/Izenda.ReportPartFieldPropsContainer.jsx